import { createContext, useState, useEffect, useCallback } from "react";
import useConfig from "../hooks/useConfig";

export const NewAuthContext = createContext({
  accessToken: null,
  refreshToken: null,
  role: null, // role object containing role and other details
  userId: null,
  user: null,
  updateAuthData: (accessToken, refreshToken, role, userId, user) => {},
  clearAuthData: () => {},
});

export const NewAuthProvider = ({ children }) => {
  const config = useConfig();
  const [authData, setAuthData] = useState(() => {
    try {
      const storedAuth = localStorage.getItem(config?.sessionKey);
      return storedAuth
        ? JSON.parse(storedAuth)
        : {
            accessToken: null,
            refreshToken: null,
            role: null,
            userId: null,
            user: null,
          };
    } catch (error) {
      console.error("Failed to load auth data from localStorage:", error);
      return {
        accessToken: null,
        refreshToken: null,
        role: null,
        userId: null,
        user: null,
      };
    }
  });

  // Save authData to localStorage whenever it changes
  useEffect(() => {
    try {
      if (authData.accessToken) {
        localStorage.setItem(config?.sessionKey, JSON.stringify(authData));
      } else {
        localStorage.removeItem(config?.sessionKey);
      }
    } catch (error) {
      console.error("Failed to save auth data to localStorage:", error);
    }
  }, [authData, config?.sessionKey]);

  // Function to update authData
  const updateAuthData = useCallback(
    (accessToken, refreshToken, role, userId, user) => {
      setAuthData({ accessToken, refreshToken, role, userId, user });
    },
    []
  );

  // Function to clear authData
  const clearAuthData = useCallback(() => {
    setAuthData({
      accessToken: null,
      refreshToken: null,
      role: null,
      userId: null,
      user: null,
    });
    localStorage.removeItem(config?.sessionKey);
    window.location.href = "/login"; // Reload the page to reset the application state
  }, [config?.sessionKey]);

  return (
    <NewAuthContext.Provider
      value={{ ...authData, updateAuthData, clearAuthData }}
    >
      {children}
    </NewAuthContext.Provider>
  );
};
